import { useCallback, useEffect, useState, type ReactNode } from "react";

import { AlphaKeysResponseSchema, type AlphaKeyRow, z } from "@legacy-manager/shared";

import { dataLonga, useT } from "../i18n";
import { mensagemDoErro, useSessao } from "../online/sessao";
import { Panel, Stat } from "../ui/atoms";
import { useErroEmToast } from "../ui/avisos";

/**
 * Painel administrativo, em `/admin`.
 *
 * **Mora numa rota de verdade, e não numa seção do hub.** O hub é do clube de
 * quem está jogando; o painel é do jogo. Misturar os dois poria um botão de
 * administração no caminho de todo mundo, para uma tela que quase ninguém pode
 * abrir.
 *
 * O jogo não tem roteador: as telas são estado. `/admin` funciona porque o
 * Caddy devolve o `index.html` para qualquer caminho, e a abertura lê
 * `location.pathname` uma vez. É o suficiente para um endereço que se digita e
 * não se navega, e não paga o preço de um roteador no pacote.
 *
 * **Quem confere o papel de verdade é o servidor.** O `role` da sessão decide o
 * que a tela mostra, e nada mais: o token não manda, e o painel do servidor
 * confere o papel contra o banco a cada chamada.
 */

const BandeirasSchema = z.object({
  flags: z.record(z.boolean()),
  defaults: z.record(z.boolean()),
  overrides: z.record(z.boolean()),
});

type Aba = "chaves" | "bandeiras";

export function Admin({ aoVoltar }: { aoVoltar: () => void }): ReactNode {
  const t = useT();
  const sessao = useSessao();
  const [aba, definirAba] = useState<Aba>("chaves");

  const papel = sessao.conta?.usuario.role;

  if (sessao.estado === "carregando") return null;
  if (sessao.estado !== "conectada") {
    return <Aviso texto={t("admin.precisaEntrar")} aoVoltar={aoVoltar} />;
  }
  if (papel !== "admin") {
    return <Aviso texto={t("admin.semPermissao")} aoVoltar={aoVoltar} />;
  }

  return (
    <div className="admin">
      <header className="admin-cabecalho">
        <strong className="admin-marca">{t("admin.titulo")}</strong>
        <nav className="admin-menu">
          <button
            type="button"
            className={aba === "chaves" ? "" : "ghost"}
            onClick={() => definirAba("chaves")}
          >
            {t("admin.menu.chaves")}
          </button>
          <button
            type="button"
            className={aba === "bandeiras" ? "" : "ghost"}
            onClick={() => definirAba("bandeiras")}
          >
            {t("admin.menu.bandeiras")}
          </button>
        </nav>
        <button type="button" className="ghost" onClick={aoVoltar}>
          {t("admin.voltar")}
        </button>
      </header>

      <main className="admin-corpo">
        {aba === "chaves" ? <ChavesDoAlfa /> : <Bandeiras />}
      </main>
    </div>
  );
}

function Aviso({ texto, aoVoltar }: { texto: string; aoVoltar: () => void }): ReactNode {
  const t = useT();
  return (
    <div className="admin">
      <main className="admin-corpo">
        <Panel title={t("admin.titulo")}>
          <p className="label-muted">{texto}</p>
          <button type="button" className="ghost" style={{ marginTop: 12 }} onClick={aoVoltar}>
            {t("admin.voltar")}
          </button>
        </Panel>
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chaves do alfa
// ---------------------------------------------------------------------------

function ChavesDoAlfa(): ReactNode {
  const t = useT();
  const sessao = useSessao();

  const [chaves, definirChaves] = useState<AlphaKeyRow[]>([]);
  const [resumo, definirResumo] = useState({ total: 0, livres: 0, usadas: 0 });
  const [quantidade, definirQuantidade] = useState(5);
  const [nota, definirNota] = useState("");
  const [ocupado, definirOcupado] = useState(false);
  const [copiada, definirCopiada] = useState<string | null>(null);
  const [erro, definirErro] = useState<string | null>(null);
  useErroEmToast(erro);

  const aplicar = useCallback((corpo: { keys: AlphaKeyRow[]; resumo: typeof resumo }) => {
    definirChaves(corpo.keys);
    definirResumo(corpo.resumo);
  }, []);

  const carregar = useCallback(async () => {
    try {
      aplicar(
        await sessao.cliente.pedir(
          { metodo: "GET", caminho: "/v1/admin/alpha-keys" },
          AlphaKeysResponseSchema,
        ),
      );
    } catch (causa) {
      definirErro(mensagemDoErro(causa));
    }
  }, [sessao.cliente, aplicar]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const gerar = async (): Promise<void> => {
    definirErro(null);
    definirOcupado(true);
    try {
      aplicar(
        await sessao.cliente.pedir(
          {
            metodo: "POST",
            caminho: "/v1/admin/alpha-keys",
            corpo: { quantidade, ...(nota.trim() ? { note: nota.trim() } : {}) },
          },
          AlphaKeysResponseSchema,
        ),
      );
      definirNota("");
    } catch (causa) {
      definirErro(mensagemDoErro(causa));
    } finally {
      definirOcupado(false);
    }
  };

  const revogar = async (code: string): Promise<void> => {
    definirErro(null);
    try {
      aplicar(
        await sessao.cliente.pedir(
          { metodo: "DELETE", caminho: `/v1/admin/alpha-keys/${code}` },
          AlphaKeysResponseSchema,
        ),
      );
    } catch (causa) {
      definirErro(mensagemDoErro(causa));
    }
  };

  /*
   * A cópia é o gesto principal desta tela.
   *
   * Uma chave existe para ser colada numa conversa. `clipboard` pode não
   * existir em contexto inseguro, e aí o `catch` deixa a seleção manual ser o
   * caminho em vez de a tela parecer quebrada.
   */
  const copiar = async (code: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(code);
      definirCopiada(code);
      setTimeout(() => definirCopiada((atual) => (atual === code ? null : atual)), 1800);
    } catch {
      definirErro(null);
    }
  };

  return (
    <>
      <Panel title={t("admin.chaves.titulo")}>
        <div className="row" style={{ gap: 18, flexWrap: "wrap" }}>
          <Stat label={t("admin.chaves.total")}>{resumo.total}</Stat>
          <Stat label={t("admin.chaves.livres")}>{resumo.livres}</Stat>
          <Stat label={t("admin.chaves.usadas")}>{resumo.usadas}</Stat>
        </div>

        <div className="row admin-gerar" style={{ gap: 10, marginTop: 16, flexWrap: "wrap" }}>
          <label className="admin-campo">
            <span className="label-muted">{t("admin.chaves.quantidade")}</span>
            <input
              type="number"
              min={1}
              max={50}
              value={quantidade}
              onChange={(evento) => definirQuantidade(Number(evento.target.value))}
              style={{ width: 84 }}
            />
          </label>
          <label className="admin-campo" style={{ flex: 1, minWidth: 180 }}>
            <span className="label-muted">{t("admin.chaves.nota")}</span>
            <input value={nota} onChange={(evento) => definirNota(evento.target.value)} maxLength={120} />
          </label>
          <button type="button" disabled={ocupado} onClick={() => void gerar()}>
            {ocupado ? t("admin.chaves.gerando") : t("admin.chaves.gerar")}
          </button>
        </div>
      </Panel>

      <Panel title={t("admin.menu.chaves")}>
        {chaves.length === 0 ? (
          <p className="label-muted">{t("admin.chaves.vazio")}</p>
        ) : (
          <ul className="admin-chaves">
            {chaves.map((chave) => (
              <li key={chave.code} data-usada={chave.usedAt ? "" : undefined}>
                <code className="admin-chave">{chave.code}</code>
                <span className="admin-estado">
                  {chave.usedAt
                    ? chave.usedByName
                      ? t("admin.chaves.usadaPor", { nome: chave.usedByName })
                      : t("admin.chaves.usadaPorAlguem")
                    : t("admin.chaves.livre")}
                </span>
                <span className="label-muted admin-quando">
                  {chave.usedAt ? dataLonga(chave.usedAt) : (chave.note ?? "")}
                </span>
                <span className="row" style={{ gap: 6 }}>
                  <button type="button" className="ghost" onClick={() => void copiar(chave.code)}>
                    {copiada === chave.code ? t("admin.chaves.copiada") : t("admin.chaves.copiar")}
                  </button>
                  {!chave.usedAt && (
                    <button type="button" className="ghost" onClick={() => void revogar(chave.code)}>
                      {t("admin.chaves.revogar")}
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </>
  );
}

// ---------------------------------------------------------------------------
// Bandeiras
// ---------------------------------------------------------------------------

function Bandeiras(): ReactNode {
  const t = useT();
  const sessao = useSessao();
  const [flags, definirFlags] = useState<Record<string, boolean>>({});
  const [erro, definirErro] = useState<string | null>(null);
  useErroEmToast(erro);

  const carregar = useCallback(async () => {
    try {
      const corpo = await sessao.cliente.pedir(
        { metodo: "GET", caminho: "/v1/admin/flags" },
        BandeirasSchema,
      );
      definirFlags(corpo.flags);
    } catch (causa) {
      definirErro(mensagemDoErro(causa));
    }
  }, [sessao.cliente]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const alternar = async (nome: string, valor: boolean): Promise<void> => {
    definirErro(null);
    try {
      const corpo = await sessao.cliente.pedir(
        { metodo: "PATCH", caminho: "/v1/admin/flags", corpo: { flags: { [nome]: valor } } },
        BandeirasSchema,
      );
      definirFlags(corpo.flags);
    } catch (causa) {
      definirErro(mensagemDoErro(causa));
    }
  };

  const nomes = Object.keys(flags).sort();

  return (
    <Panel title={t("admin.bandeiras.titulo")}>
      {nomes.length === 0 ? (
        <p className="label-muted">{t("admin.bandeiras.vazio")}</p>
      ) : (
        <ul className="admin-bandeiras">
          {nomes.map((nome) => (
            <li key={nome}>
              <span className="admin-bandeira-nome">
                {nome === "alfa_fechado" ? t("admin.bandeiras.alfaFechado") : nome}
              </span>
              <button
                type="button"
                className={flags[nome] ? "" : "ghost"}
                onClick={() => void alternar(nome, !flags[nome])}
              >
                {flags[nome] ? t("admin.bandeiras.ligada") : t("admin.bandeiras.desligada")}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
